import { API_BASE } from '../lib/apiOrigin';
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  X,
  Plus,
  Trash2,
  Send,
  Save,
  Eye,
  FileText,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
  Mail,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

const API = API_BASE;

const STATUS_COLORS = {
  draft: "bg-zinc-700 text-zinc-200",
  sent: "bg-sky-500 text-white",
  accepted: "bg-emerald-500 text-white",
  declined: "bg-red-500 text-white",
  revised: "bg-amber-500 text-black",
};

const fmtMoney = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * QuoteBuilderDialog — Admin builds, edits and sends a quote response to a lead.
 * Props: open, onOpenChange, lead, token (X-Admin-Token).
 */
const QuoteBuilderDialog = ({ open, onOpenChange, lead, token }) => {
  const [history, setHistory] = useState([]);
  const [active, setActive] = useState(null); // currently edited quote response
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("idle"); // idle|saving|sending|done|error
  const [errorMsg, setErrorMsg] = useState("");

  const authHeaders = { "X-Admin-Token": token };

  const loadHistory = useCallback(async () => {
    if (!lead?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/leads/${lead.id}/quote-responses`,
        { headers: authHeaders }
      );
      setHistory(data);
      // Open most recent draft / latest if any
      if (data.length > 0) setActive(data[0]);
      else setActive(null);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Failed to load quote history.");
    } finally {
      setLoading(false);
    }
  }, [lead?.id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      setErrorMsg("");
      setStatus("idle");
      loadHistory();
    }
  }, [open, loadHistory]);

  // Recompute subtotal/total live whenever line items / base / discount change
  const recomputeTotals = (q) => {
    if (!q) return q;
    const addOnSum = (q.line_items || []).reduce(
      (s, li) => s + (Number(li.qty) || 0) * (Number(li.unit_price) || 0),
      0
    );
    const subtotal = Number(q.base_install_price || 0) + addOnSum;
    const total = Math.max(0, subtotal - Number(q.discount || 0));
    return {
      ...q,
      subtotal: Math.round(subtotal * 100) / 100,
      total: Math.round(total * 100) / 100,
    };
  };

  const patchActive = (patch) => {
    setActive((prev) => recomputeTotals({ ...prev, ...patch }));
  };

  const patchLineItem = (idx, patch) => {
    setActive((prev) => {
      const items = [...(prev.line_items || [])];
      items[idx] = { ...items[idx], ...patch };
      items[idx].line_total =
        (Number(items[idx].qty) || 0) * (Number(items[idx].unit_price) || 0);
      return recomputeTotals({ ...prev, line_items: items });
    });
  };

  const addLineItem = () => {
    setActive((prev) =>
      recomputeTotals({
        ...prev,
        line_items: [
          ...(prev.line_items || []),
          { id: crypto.randomUUID(), label: "New item", qty: 1, unit_price: 0, line_total: 0 },
        ],
      })
    );
  };

  const removeLineItem = (idx) => {
    setActive((prev) =>
      recomputeTotals({
        ...prev,
        line_items: (prev.line_items || []).filter((_, i) => i !== idx),
      })
    );
  };

  const createNewDraft = async () => {
    setStatus("saving");
    setErrorMsg("");
    try {
      const { data } = await axios.post(
        `${API}/admin/leads/${lead.id}/quote-responses`,
        {
          customer_notes: "",
          admin_notes: "",
          payment_instructions: "",
          discount: 0,
        },
        { headers: authHeaders }
      );
      setHistory((h) => [data, ...h]);
      setActive(data);
      setStatus("done");
    } catch (e) {
      setStatus("error");
      setErrorMsg(e?.response?.data?.detail || "Failed to create draft.");
    }
  };

  const saveDraft = async () => {
    if (!active?.id) return;
    setStatus("saving");
    setErrorMsg("");
    try {
      const payload = {
        line_items: active.line_items,
        base_install_price: Number(active.base_install_price || 0),
        discount: Number(active.discount || 0),
        customer_notes: active.customer_notes || "",
        admin_notes: active.admin_notes || "",
        payment_instructions: active.payment_instructions || "",
      };
      const { data } = await axios.patch(
        `${API}/admin/quote-responses/${active.id}`,
        payload,
        { headers: authHeaders }
      );
      setActive(data);
      setHistory((h) => h.map((q) => (q.id === data.id ? data : q)));
      setStatus("done");
    } catch (e) {
      setStatus("error");
      setErrorMsg(e?.response?.data?.detail || "Failed to save.");
    }
  };

  const sendQuote = async () => {
    if (!active?.id) return;
    if (
      !window.confirm(
        `Send this quote (${fmtMoney(active.total)}) to ${lead.email}?\n\nThis will email the customer with a PDF + Accept/Decline links and mark the lead as QUOTED.`
      )
    )
      return;
    setStatus("sending");
    setErrorMsg("");
    try {
      // Save first so latest edits are persisted
      await saveDraft();
      const { data } = await axios.post(
        `${API}/admin/quote-responses/${active.id}/send`,
        {},
        { headers: authHeaders }
      );
      setActive(data);
      setHistory((h) => h.map((q) => (q.id === data.id ? data : q)));
      setStatus("done");
      // Notify parent (Admin leads list) that this lead was just quoted so it can refresh.
      window.dispatchEvent(new CustomEvent("xa:lead-quoted", { detail: { leadId: lead.id } }));
    } catch (e) {
      setStatus("error");
      setErrorMsg(e?.response?.data?.detail || "Failed to send.");
    }
  };

  const previewPdf = () => {
    if (!active?.id) return;
    // Open PDF in new tab with token (auth header lost — use blob approach instead)
    axios
      .get(`${API}/admin/quote-responses/${active.id}/preview-pdf`, {
        headers: authHeaders,
        responseType: "blob",
      })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      })
      .catch((e) =>
        setErrorMsg(e?.response?.data?.detail || "Failed to preview PDF.")
      );
  };

  const updateStatus = async (newStatus) => {
    if (!active?.id) return;
    try {
      const { data } = await axios.patch(
        `${API}/admin/quote-responses/${active.id}`,
        { status: newStatus },
        { headers: authHeaders }
      );
      setActive(data);
      setHistory((h) => h.map((q) => (q.id === data.id ? data : q)));
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Failed to update status.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-builder-dialog"
        className="bg-zinc-950 border border-white/10 rounded-none w-[calc(100vw-1rem)] sm:w-auto max-w-5xl p-0 gap-0 overflow-hidden max-h-[94vh] flex flex-col"
      >
        <DialogTitle className="sr-only">Build / Send Quote</DialogTitle>
        <DialogDescription className="sr-only">
          Admin quote response builder. Edit line items, totals and dispatch via email.
        </DialogDescription>

        <button
          data-testid="quote-builder-close"
          onClick={() => onOpenChange(false)}
          className="absolute top-4 right-4 z-20 text-zinc-400 hover:text-white"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* HEADER */}
        <div className="bg-gradient-to-br from-sky-500 to-sky-600 px-6 py-6 shrink-0">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-white mb-2">
            Quote Builder
          </div>
          <h3 className="font-display font-black text-white text-2xl sm:text-3xl leading-tight">
            {lead?.name || "Lead"} · {lead?.service || "Service"}
          </h3>
          <div className="text-white text-sm sm:text-base mt-2 font-medium">
            {lead?.email} · {lead?.phone}
          </div>
        </div>

        <div className="overflow-y-auto px-5 sm:px-6 py-5 flex-1">
          {loading && (
            <div className="text-zinc-400 text-sm py-12 text-center">Loading…</div>
          )}

          {!loading && history.length === 0 && (
            <div className="py-14 text-center space-y-5">
              <FileText size={40} className="mx-auto text-sky-400" />
              <div className="text-zinc-100 text-base max-w-md mx-auto leading-relaxed">
                No quotes yet for this lead. Create a draft to auto-populate from the customer's selections.
              </div>
              <button
                data-testid="qb-create-draft"
                onClick={createNewDraft}
                className="btn-primary text-base px-7 py-3.5"
              >
                <Plus size={18} /> Create Draft Quote
              </button>
            </div>
          )}

          {!loading && active && (
            <div className="space-y-6">
              {/* History selector */}
              {history.length > 1 && (
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="eyebrow !text-xs !text-zinc-200 font-semibold">History</span>
                  {history.map((q, i) => (
                    <button
                      key={q.id}
                      data-testid={`qb-history-${q.id}`}
                      onClick={() => setActive(q)}
                      className={`text-sm px-4 py-2 border transition-colors font-semibold ${
                        active.id === q.id
                          ? "border-sky-400 bg-sky-500/15 text-white"
                          : "border-white/15 text-zinc-200 hover:border-white/30"
                      }`}
                    >
                      #{history.length - i} · {fmtMoney(q.total)}
                      <span
                        className={`ml-2 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold ${
                          STATUS_COLORS[q.status] || "bg-zinc-700 text-zinc-200"
                        }`}
                      >
                        {q.status}
                      </span>
                    </button>
                  ))}
                  <button
                    onClick={createNewDraft}
                    data-testid="qb-new-revision"
                    className="text-sm px-4 py-2 border border-dashed border-white/25 text-zinc-200 hover:border-sky-400 hover:text-sky-400 transition-colors font-semibold"
                  >
                    <Plus size={14} className="inline" /> New
                  </button>
                </div>
              )}

              {/* Status + meta */}
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span
                  className={`px-4 py-2 font-display font-black uppercase tracking-wider text-sm ${
                    STATUS_COLORS[active.status] || "bg-zinc-700 text-zinc-200"
                  }`}
                  data-testid="qb-status-pill"
                >
                  {active.status}
                </span>
                <select
                  data-testid="qb-status-select"
                  value={active.status}
                  onChange={(e) => updateStatus(e.target.value)}
                  className="bg-zinc-900 border border-white/15 text-zinc-100 text-sm font-semibold uppercase tracking-[0.12em] py-2 px-3 outline-none focus:border-sky-400"
                >
                  {["draft", "sent", "accepted", "declined", "revised"].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {active.sent_at && (
                  <span className="text-zinc-300 text-sm">
                    Sent {new Date(active.sent_at).toLocaleString()} to{" "}
                    <span className="text-white font-semibold">{active.email_sent_to}</span>
                  </span>
                )}
                {active.revision_count > 0 && (
                  <span className="text-amber-300 font-semibold text-sm">
                    <RotateCcw size={13} className="inline" /> Revision {active.revision_count}
                  </span>
                )}
              </div>

              {/* Base price + discount */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="eyebrow !text-xs !text-zinc-200 mb-2.5 block font-semibold">
                    Base installation price (USD)
                  </label>
                  <input
                    data-testid="qb-base-price"
                    type="number"
                    min={0}
                    step={0.01}
                    value={active.base_install_price || 0}
                    onChange={(e) =>
                      patchActive({ base_install_price: parseFloat(e.target.value || "0") })
                    }
                    className="w-full bg-transparent border-b border-white/20 focus:border-sky-400 text-white py-3 outline-none text-2xl font-display font-black"
                  />
                </div>
                <div>
                  <label className="eyebrow !text-xs !text-zinc-200 mb-2.5 block font-semibold">
                    Discount (USD)
                  </label>
                  <input
                    data-testid="qb-discount"
                    type="number"
                    min={0}
                    step={0.01}
                    value={active.discount || 0}
                    onChange={(e) =>
                      patchActive({ discount: parseFloat(e.target.value || "0") })
                    }
                    className="w-full bg-transparent border-b border-white/20 focus:border-sky-400 text-white py-3 outline-none text-2xl font-display font-black"
                  />
                </div>
              </div>

              {/* Line items table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="eyebrow !text-xs !text-zinc-200 font-semibold">Line items (add-ons)</div>
                  <button
                    data-testid="qb-add-line"
                    onClick={addLineItem}
                    className="text-sm text-sky-400 hover:text-sky-300 flex items-center gap-1.5 font-semibold"
                  >
                    <Plus size={14} /> Add item
                  </button>
                </div>
                <div className="border border-white/15">
                  <div className="grid grid-cols-12 gap-2 px-3 py-3 bg-zinc-900 text-xs uppercase tracking-wider text-zinc-200 font-bold">
                    <div className="col-span-6">Item</div>
                    <div className="col-span-2 text-center">Qty</div>
                    <div className="col-span-2 text-right">Unit</div>
                    <div className="col-span-1 text-right">Total</div>
                    <div className="col-span-1" />
                  </div>
                  {(active.line_items || []).length === 0 && (
                    <div className="px-3 py-8 text-center text-zinc-300 text-sm">
                      No line items. Use "Add item" or create a fresh draft to auto-populate.
                    </div>
                  )}
                  {(active.line_items || []).map((li, idx) => {
                    const lineTotal =
                      (Number(li.qty) || 0) * (Number(li.unit_price) || 0);
                    return (
                      <div
                        key={li.id || idx}
                        data-testid={`qb-line-${idx}`}
                        className="grid grid-cols-12 gap-2 px-3 py-2.5 border-t border-white/5 items-center"
                      >
                        <input
                          data-testid={`qb-line-label-${idx}`}
                          value={li.label}
                          onChange={(e) => patchLineItem(idx, { label: e.target.value })}
                          className="col-span-6 bg-transparent text-white text-base py-1.5 outline-none border-b border-transparent focus:border-sky-400 font-medium"
                        />
                        <input
                          data-testid={`qb-line-qty-${idx}`}
                          type="number"
                          step={1}
                          min={0}
                          value={li.qty || 0}
                          onChange={(e) =>
                            patchLineItem(idx, { qty: parseFloat(e.target.value || "0") })
                          }
                          className="col-span-2 bg-transparent text-white text-base py-1.5 outline-none border-b border-transparent focus:border-sky-400 text-center font-semibold"
                        />
                        <input
                          data-testid={`qb-line-price-${idx}`}
                          type="number"
                          step={0.01}
                          min={0}
                          value={li.unit_price || 0}
                          onChange={(e) =>
                            patchLineItem(idx, { unit_price: parseFloat(e.target.value || "0") })
                          }
                          className="col-span-2 bg-transparent text-white text-base py-1.5 outline-none border-b border-transparent focus:border-sky-400 text-right font-semibold"
                        />
                        <div className="col-span-1 text-right text-sky-300 font-bold text-base">
                          {fmtMoney(lineTotal)}
                        </div>
                        <button
                          data-testid={`qb-line-remove-${idx}`}
                          onClick={() => removeLineItem(idx)}
                          className="col-span-1 text-zinc-400 hover:text-red-400 flex justify-end"
                          title="Remove"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Totals summary */}
              <div className="border border-white/15 bg-zinc-950/70 p-5 space-y-3">
                <div className="flex justify-between text-base text-zinc-200">
                  <span className="font-semibold">Subtotal</span>
                  <span data-testid="qb-subtotal" className="font-bold text-white">
                    {fmtMoney(active.subtotal)}
                  </span>
                </div>
                {active.discount > 0 && (
                  <div className="flex justify-between text-base text-zinc-200">
                    <span className="font-semibold">Discount</span>
                    <span className="text-red-400 font-bold">−{fmtMoney(active.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-white/15">
                  <span className="font-display font-black uppercase tracking-wider text-white text-base">
                    Total
                  </span>
                  <span data-testid="qb-total" className="font-display font-black text-sky-400 text-3xl sm:text-4xl tracking-tight">
                    {fmtMoney(active.total)}
                  </span>
                </div>
              </div>

              {/* Notes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="eyebrow !text-xs !text-zinc-200 mb-2.5 block font-semibold">
                    Customer-facing notes
                  </label>
                  <textarea
                    data-testid="qb-customer-notes"
                    rows={4}
                    value={active.customer_notes || ""}
                    onChange={(e) => patchActive({ customer_notes: e.target.value })}
                    placeholder="Visible to the customer in the email"
                    className="w-full bg-transparent border border-white/15 focus:border-sky-400 text-white py-3 px-3.5 outline-none resize-y placeholder:text-zinc-500 text-base"
                  />
                </div>
                <div>
                  <label className="eyebrow !text-xs !text-zinc-200 mb-2.5 block font-semibold">
                    Internal admin notes
                  </label>
                  <textarea
                    data-testid="qb-admin-notes"
                    rows={4}
                    value={active.admin_notes || ""}
                    onChange={(e) => patchActive({ admin_notes: e.target.value })}
                    placeholder="Private — never sent to the customer"
                    className="w-full bg-transparent border border-white/15 focus:border-sky-400 text-white py-3 px-3.5 outline-none resize-y placeholder:text-zinc-500 text-base"
                  />
                </div>
              </div>

              {/* Payment instructions */}
              <div>
                <label className="eyebrow !text-xs !text-zinc-200 mb-2.5 block font-semibold">
                  Payment instructions (optional)
                </label>
                <textarea
                  data-testid="qb-payment-instructions"
                  rows={3}
                  value={active.payment_instructions || ""}
                  onChange={(e) => patchActive({ payment_instructions: e.target.value })}
                  placeholder="e.g. 50% deposit via Zelle on scheduling, balance on completion"
                  className="w-full bg-transparent border border-white/15 focus:border-sky-400 text-white py-3 px-3.5 outline-none resize-y placeholder:text-zinc-500 text-base"
                />
              </div>

              {/* Email delivery tracking (Resend webhooks) */}
              {active.resend_message_id && (
                <div data-testid="qb-email-tracking" className="border border-white/10 bg-zinc-950/60 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Mail size={14} className="text-sky-400" />
                    <span className="eyebrow !text-xs !text-zinc-200 font-semibold">Email delivery</span>
                  </div>
                  {!active.email_events_log || active.email_events_log.length === 0 ? (
                    <div className="text-zinc-400 text-sm">
                      Waiting for delivery events from Resend… (events appear here automatically)
                    </div>
                  ) : (
                    <ol className="space-y-1.5">
                      {active.email_events_log.map((ev, i) => {
                        const colors = {
                          "email.sent": "text-zinc-300",
                          "email.delivered": "text-emerald-300",
                          "email.opened": "text-sky-300",
                          "email.clicked": "text-sky-300",
                          "email.bounced": "text-red-300",
                          "email.complained": "text-red-300",
                          "email.delivery_delayed": "text-amber-300",
                        };
                        const c = colors[ev.type] || "text-zinc-300";
                        return (
                          <li key={i} className="text-sm flex items-center gap-2">
                            <span className={`inline-block h-2 w-2 rounded-full bg-current ${c}`} />
                            <span className={`font-semibold uppercase tracking-wider text-xs ${c}`}>
                              {ev.type.replace("email.", "")}
                            </span>
                            <span className="text-zinc-400 text-xs">
                              {ev.at ? new Date(ev.at).toLocaleString() : ""}
                            </span>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              )}

              {/* Status banner */}
              {status === "done" && active.status === "sent" && active.resend_message_id && (
                <div data-testid="qb-sent-banner" className="flex items-start gap-3 bg-emerald-500/15 border border-emerald-400/40 px-4 py-4">
                  <CheckCircle2 size={22} className="text-emerald-300 shrink-0 mt-0.5" />
                  <div className="text-sm leading-relaxed">
                    <div className="text-emerald-200 font-bold text-base">Quote sent successfully</div>
                    <div className="text-zinc-200 mt-1">
                      Delivered to <span className="text-white font-semibold">{active.email_sent_to}</span> ·
                      Resend ID: <code className="text-emerald-300 text-xs">{active.resend_message_id}</code>
                    </div>
                    <div className="text-zinc-300 mt-0.5 text-xs">
                      Lead status auto-updated to <strong className="text-white">QUOTED</strong>. Send date saved.
                    </div>
                  </div>
                </div>
              )}
              {status === "done" && !(active.status === "sent" && active.resend_message_id) && (
                <div data-testid="qb-success" className="flex items-center gap-2 text-emerald-300 text-base font-semibold">
                  <CheckCircle2 size={16} /> Saved.
                </div>
              )}
              {errorMsg && (
                <div data-testid="qb-error" className="flex items-start gap-2 text-red-200 text-base bg-red-500/10 border border-red-500/30 px-4 py-3 font-medium">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" /> {errorMsg}
                </div>
              )}
            </div>
          )}
        </div>

        {/* FOOTER ACTIONS */}
        {active && (
          <div className="border-t border-white/15 bg-zinc-950 px-5 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3 shrink-0">
            <button
              data-testid="qb-preview-pdf"
              onClick={previewPdf}
              className="text-zinc-200 hover:text-sky-400 text-sm flex items-center gap-2 font-semibold border border-white/15 hover:border-sky-400 px-4 py-2.5 transition-colors"
            >
              <Eye size={15} /> Preview PDF
            </button>
            <div className="flex items-center gap-3">
              <button
                data-testid="qb-save-draft"
                onClick={saveDraft}
                disabled={status === "saving" || status === "sending"}
                className="inline-flex items-center gap-2 border border-white/25 hover:border-white/50 text-white font-display font-bold uppercase tracking-[0.12em] text-base px-5 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={16} />
                {status === "saving" ? "Saving…" : "Save Draft"}
              </button>
              <button
                data-testid="qb-send-quote"
                onClick={sendQuote}
                disabled={status === "saving" || status === "sending"}
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white font-display font-bold uppercase tracking-[0.12em] text-base px-6 py-3 transition-colors shadow-[0_4px_18px_rgba(14,165,233,0.4)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={16} />
                {status === "sending"
                  ? "Sending…"
                  : active.status === "sent" || active.status === "revised"
                  ? "Resend Quote"
                  : "Send Quote"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteBuilderDialog;
